import React, { useState, useEffect } from 'react';
import './VendorCatalog.css';
import DataSourceIndicator from './DataSourceIndicator';

interface Vendor {
  id: string;
  name: string;
  code: string;
  vendorType: string;
  integrationStatus: string;
  contactInfo: {
    email: string;
    phone: string;
    address: string;
    website: string;
    primaryContact: string;
  };
  rating: number;
  qualityRating: number;
  deliveryRating: number;
  serviceRating: number;
  performanceMetrics: {
    onTimeDelivery: number;
    orderAccuracy: number;
    qualityScore: number;
    responseTime: string;
  };
  isActive: boolean;
  lastCatalogSync: string;
}

interface SupplierItem {
  id: string;
  supplierSku: string;
  supplierName: string;
  barcode?: string;
  upc?: string;
  brand?: string;
  manufacturer?: string;
  unitsPerCase: number;
  casesPerPallet?: number;
  costPerPack: number;
  costPerBaseUnit: number;
  packDimensions?: {
    length: number;
    width: number;
    height: number;
    weight: number;
    unit: string;
    weightUnit: string;
  };
  nutritionalFacts?: any;
  allergenInformation?: any;
  productImages?: Array<{
    id: string;
    url: string;
    alt: string;
    isPrimary: boolean;
    imageType: string;
  }>;
  availabilityStatus: string;
  updatedAt: string;
}

interface VendorCatalogProps {
  enterpriseId: string;
}

export const VendorCatalog: React.FC<VendorCatalogProps> = ({ enterpriseId }) => {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [supplierItems, setSupplierItems] = useState<SupplierItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<SupplierItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBy, setFilterBy] = useState('all');
  const [dataSource, setDataSource] = useState<any>(null);

  useEffect(() => {
    loadVendors();
  }, [enterpriseId]);

  const loadVendors = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/vendors/${enterpriseId}`);
      const data = await response.json();
      
      if (data.success !== false) {
        setVendors(data.data || data.vendors || []);
        setDataSource(data.dataSource);
      }
    } catch (error) {
      console.error('Error loading vendors:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSupplierItems = async (vendorId: string) => {
    try {
      const response = await fetch(`/api/vendors/${vendorId}/items`);
      const data = await response.json();
      
      if (data.success !== false) {
        setSupplierItems(data.data?.items || data.items || []);
      }
    } catch (error) {
      console.error('Error loading supplier items:', error);
    }
  };

  const handleVendorSelect = (vendor: Vendor) => {
    setSelectedVendor(vendor);
    setSelectedItem(null);
    loadSupplierItems(vendor.id);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getRatingColor = (rating: number) => {
    if (rating >= 4.5) return '#10b981';
    if (rating >= 4.0) return '#f59e0b';
    if (rating >= 3.5) return '#f97316';
    return '#ef4444';
  };

  const filteredVendors = vendors.filter(vendor => {
    const matchesSearch = vendor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         vendor.code.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterBy === 'all' || 
                         (filterBy === 'active' && vendor.isActive) ||
                         (filterBy === 'api' && vendor.integrationStatus === 'api') ||
                         (filterBy === 'distributor' && vendor.vendorType === 'distributor');
    
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="vendor-catalog">
        <div className="loading-spinner">Loading vendor catalog...</div>
      </div>
    );
  }

  return (
    <div className="vendor-catalog">
      <div className="catalog-header">
        <h2>Vendor Catalog Management</h2>
        {dataSource && <DataSourceIndicator dataSource={dataSource} />}
        
        <div className="catalog-controls">
          <div className="search-filter-row">
            <input
              type="text"
              placeholder="Search vendors..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <select
              value={filterBy}
              onChange={(e) => setFilterBy(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Vendors</option>
              <option value="active">Active Only</option>
              <option value="api">API Integrated</option>
              <option value="distributor">Distributors</option>
            </select>
          </div>
        </div>
      </div>

      <div className="catalog-content">
        <div className="vendors-panel">
          <h3>Vendors ({filteredVendors.length})</h3>
          <div className="vendors-list">
            {filteredVendors.map(vendor => (
              <div
                key={vendor.id}
                className={`vendor-card ${selectedVendor?.id === vendor.id ? 'selected' : ''}`}
                onClick={() => handleVendorSelect(vendor)}
              >
                <div className="vendor-header">
                  <div className="vendor-name">
                    <h4>{vendor.name}</h4>
                    <span className="vendor-code">{vendor.code}</span>
                  </div>
                  <div className="vendor-status">
                    <span className={`status-badge ${vendor.isActive ? 'active' : 'inactive'}`}>
                      {vendor.isActive ? 'Active' : 'Inactive'}
                    </span>
                    <span className={`integration-badge ${vendor.integrationStatus}`}>
                      {vendor.integrationStatus.toUpperCase()}
                    </span>
                  </div>
                </div>
                
                <div className="vendor-metrics">
                  <div className="rating-summary">
                    <span 
                      className="overall-rating"
                      style={{ color: getRatingColor(vendor.rating) }}
                    >
                      ★ {vendor.rating.toFixed(1)}
                    </span>
                    <div className="rating-breakdown">
                      <span>Q: {vendor.qualityRating.toFixed(1)}</span>
                      <span>D: {vendor.deliveryRating.toFixed(1)}</span>
                      <span>S: {vendor.serviceRating.toFixed(1)}</span>
                    </div>
                  </div>
                  
                  <div className="performance-metrics">
                    <div className="metric">
                      <span className="metric-label">On-Time:</span>
                      <span className="metric-value">{vendor.performanceMetrics.onTimeDelivery}%</span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Accuracy:</span>
                      <span className="metric-value">{vendor.performanceMetrics.orderAccuracy}%</span>
                    </div>
                  </div>
                </div>

                <div className="vendor-contact">
                  <div className="contact-info">
                    <span>{vendor.contactInfo.primaryContact}</span>
                    <span>{vendor.contactInfo.phone}</span>
                  </div>
                  <div className="last-sync">
                    Last sync: {formatDate(vendor.lastCatalogSync)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {selectedVendor && (
          <div className="vendor-details-panel">
            <div className="vendor-details-header">
              <h3>{selectedVendor.name} - Product Catalog</h3>
              <div className="vendor-actions">
                <button className="btn-sync">Sync Catalog</button>
                <button className="btn-manage">Manage Vendor</button>
              </div>
            </div>

            <div className="supplier-items-grid">
              {supplierItems.map(item => (
                <div
                  key={item.id}
                  className={`supplier-item-card ${selectedItem?.id === item.id ? 'selected' : ''}`}
                  onClick={() => setSelectedItem(item)}
                >
                  <div className="item-image">
                    {item.productImages?.find(img => img.isPrimary) ? (
                      <img 
                        src={item.productImages.find(img => img.isPrimary)?.url} 
                        alt={item.supplierName}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="no-image">No Image</div>
                    )}
                  </div>
                  
                  <div className="item-details">
                    <h4 className="item-name">{item.supplierName}</h4>
                    <div className="item-codes">
                      <span className="sku">SKU: {item.supplierSku}</span>
                      {item.barcode && (
                        <span className="barcode">UPC: {item.barcode}</span>
                      )}
                    </div>
                    
                    {item.brand && (
                      <div className="item-brand">{item.brand}</div>
                    )}
                    
                    <div className="item-pricing">
                      <div className="price-row">
                        <span>Pack:</span>
                        <span className="price">{formatCurrency(item.costPerPack)}</span>
                      </div>
                      <div className="price-row">
                        <span>Unit:</span>
                        <span className="price">{formatCurrency(item.costPerBaseUnit)}</span>
                      </div>
                    </div>
                    
                    <div className="item-specs">
                      <span className="units-per-case">{item.unitsPerCase} units/case</span>
                      {item.casesPerPallet && (
                        <span className="cases-per-pallet">{item.casesPerPallet} cases/pallet</span>
                      )}
                    </div>
                    
                    <div className="item-status">
                      <span className={`availability-status ${item.availabilityStatus}`}>
                        {item.availabilityStatus.charAt(0).toUpperCase() + item.availabilityStatus.slice(1)}
                      </span>
                      <span className="last-updated">
                        Updated: {formatDate(item.updatedAt)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {selectedItem && (
          <div className="item-details-panel">
            <div className="item-details-header">
              <h3>{selectedItem.supplierName}</h3>
              <button 
                className="close-btn"
                onClick={() => setSelectedItem(null)}
              >
                ×
              </button>
            </div>

            <div className="item-details-content">
              <div className="details-section">
                <h4>Product Information</h4>
                <div className="details-grid">
                  <div className="detail-item">
                    <label>Supplier SKU:</label>
                    <span>{selectedItem.supplierSku}</span>
                  </div>
                  {selectedItem.barcode && (
                    <div className="detail-item">
                      <label>Barcode:</label>
                      <span>{selectedItem.barcode}</span>
                    </div>
                  )}
                  {selectedItem.upc && (
                    <div className="detail-item">
                      <label>UPC:</label>
                      <span>{selectedItem.upc}</span>
                    </div>
                  )}
                  {selectedItem.brand && (
                    <div className="detail-item">
                      <label>Brand:</label>
                      <span>{selectedItem.brand}</span>
                    </div>
                  )}
                  {selectedItem.manufacturer && (
                    <div className="detail-item">
                      <label>Manufacturer:</label>
                      <span>{selectedItem.manufacturer}</span>
                    </div>
                  )}
                </div>
              </div>

              {selectedItem.packDimensions && (
                <div className="details-section">
                  <h4>Package Specifications</h4>
                  <div className="details-grid">
                    <div className="detail-item">
                      <label>Dimensions:</label>
                      <span>
                        {selectedItem.packDimensions.length}" × {selectedItem.packDimensions.width}" × {selectedItem.packDimensions.height}"
                      </span>
                    </div>
                    <div className="detail-item">
                      <label>Weight:</label>
                      <span>
                        {selectedItem.packDimensions.weight} {selectedItem.packDimensions.weightUnit}
                      </span>
                    </div>
                    <div className="detail-item">
                      <label>Units per Case:</label>
                      <span>{selectedItem.unitsPerCase}</span>
                    </div>
                    {selectedItem.casesPerPallet && (
                      <div className="detail-item">
                        <label>Cases per Pallet:</label>
                        <span>{selectedItem.casesPerPallet}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="details-section">
                <h4>Pricing Information</h4>
                <div className="details-grid">
                  <div className="detail-item">
                    <label>Cost per Pack:</label>
                    <span className="price-highlight">{formatCurrency(selectedItem.costPerPack)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Cost per Base Unit:</label>
                    <span className="price-highlight">{formatCurrency(selectedItem.costPerBaseUnit)}</span>
                  </div>
                </div>
              </div>

              {selectedItem.nutritionalFacts && (
                <div className="details-section">
                  <h4>Nutritional Information</h4>
                  <div className="nutrition-panel">
                    {selectedItem.nutritionalFacts.servingSize && (
                      <div className="nutrition-item">
                        <span>Serving Size: {selectedItem.nutritionalFacts.servingSize} {selectedItem.nutritionalFacts.servingUnit}</span>
                      </div>
                    )}
                    {selectedItem.nutritionalFacts.calories && (
                      <div className="nutrition-item">
                        <span>Calories: {selectedItem.nutritionalFacts.calories}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {selectedItem.allergenInformation && (
                <div className="details-section">
                  <h4>Allergen Information</h4>
                  <div className="allergen-panel">
                    {selectedItem.allergenInformation.contains?.length > 0 && (
                      <div className="allergen-group">
                        <strong>Contains:</strong>
                        <span>{selectedItem.allergenInformation.contains.join(', ')}</span>
                      </div>
                    )}
                    {selectedItem.allergenInformation.mayContain?.length > 0 && (
                      <div className="allergen-group">
                        <strong>May Contain:</strong>
                        <span>{selectedItem.allergenInformation.mayContain.join(', ')}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {selectedItem.productImages && selectedItem.productImages.length > 0 && (
                <div className="details-section">
                  <h4>Product Images</h4>
                  <div className="image-gallery">
                    {selectedItem.productImages.map(image => (
                      <div key={image.id} className="gallery-image">
                        <img 
                          src={image.url} 
                          alt={image.alt}
                          className={image.isPrimary ? 'primary-image' : ''}
                        />
                        <span className="image-type">{image.imageType}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};